import { message } from 'antd';
import { action, computed, observable } from 'mobx';
import { SecurityService } from '../../common/services';
import { TestProjectChange } from '../../common/types';
import { endpoints } from '../../../config';
import { PackageListModel } from '../misc';
import { TestProjectService } from '../services';
import { TestProjectBaseline, TestProjectListModel } from '../types';
import type { TestProjectInputsChanged } from '../types';
import TestProjectsVisualStore from './TestProjectsVisualStore';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

// Owns project data, baseline data and their mutations for one fixed project/Test Project identity.
// Dashboard selection and import review use separate instances; route and editor state stay with their owners.
// Replace this instance when selection changes; pending reads and writes retain their original project IDs.
export default class TestProjectDataStore {
    @observable
    testProject: TestProjectListModel | undefined;

    @observable
    projectStatus: LoadStatus = 'idle';

    @observable
    testProjectLoadError: string | undefined;

    @observable
    testProjectBaselines: TestProjectBaseline[] = [];

    @observable
    baselinesStatus: LoadStatus = 'idle';

    @observable
    baselinesLoadError: string | undefined;

    @observable
    isSubmittingBaseline = false;

    @observable
    deletingPackageId: string | null = null;

    @observable.ref
    inputsChange: TestProjectInputsChanged | undefined;

    @observable
    inputRefreshStatus: LoadStatus = 'idle';

    // Fixed IDs prevent cross-project writes; these counters also prevent older reads replacing newer reads.
    private projectLoadRequest = 0;
    private baselinesLoadRequest = 0;

    constructor(
        readonly projectId: string,
        readonly testProjectId: string,
        private service: TestProjectService,
        readonly testProjectsStore: TestProjectsVisualStore
    ) {}

    @computed
    get isLoadingTestProject() {
        return this.projectStatus === 'loading';
    }

    @computed
    get isLoadingBaselines() {
        return this.baselinesStatus === 'loading';
    }

    // A failed refresh may retain data for display, but retained data does not authorize editing.
    @computed
    get canUseProject() {
        return this.projectStatus === 'ready' && !!this.testProject;
    }

    @computed
    get canEditBaselines() {
        return this.canUseProject && this.baselinesStatus === 'ready';
    }

    @computed
    get packageListItems() {
        if (!this.testProject || this.testProjectsStore.loadingPackages) {
            return [];
        }

        return this.testProject.packageIds.map(
            id => new PackageListModel(id, this.testProjectsStore.getPackageById(id)?.name ?? id)
        );
    }

    @computed
    get orderedTestProjectTopics() {
        return this.testProject?.topics.slice().sort((a, b) => a.name.localeCompare(b.name)) ?? [];
    }

    @computed
    get baselineUploadUrl() {
        if (!this.canUseProject) {
            return '';
        }

        const path = `${endpoints.manageUrl}projects/${this.projectId}/test-projects/${this.testProjectId}/baselines/import`;
        return `${path}?access_token=${encodeURIComponent(SecurityService.token)}`;
    }

    @action.bound
    async loadTestProject(): Promise<boolean> {
        const request = ++this.projectLoadRequest;
        this.projectStatus = 'loading';
        this.testProjectLoadError = undefined;

        try {
            const result = await this.service.getTestProject(this.projectId, this.testProjectId);

            if (request !== this.projectLoadRequest) {
                return false;
            }

            if (result.isErr()) {
                throw result.error;
            }

            this.testProject = result.value;
            this.projectStatus = 'ready';
            return true;
        } catch (error) {
            if (request === this.projectLoadRequest) {
                this.projectStatus = 'error';
                this.testProjectLoadError = 'Failed to load test project';
                console.error(error);
            }

            return false;
        }
    }

    @action.bound
    async loadTestProjectBaselines(): Promise<boolean> {
        const request = ++this.baselinesLoadRequest;
        this.baselinesStatus = 'loading';
        this.baselinesLoadError = undefined;

        try {
            const result = await this.service.getTestProjectBaselines(this.projectId, this.testProjectId);

            if (request !== this.baselinesLoadRequest) {
                return false;
            }

            if (result.isErr()) {
                throw result.error;
            }

            this.testProjectBaselines = result.value;
            this.baselinesStatus = 'ready';
            return true;
        } catch (error) {
            if (request === this.baselinesLoadRequest) {
                this.baselinesStatus = 'error';
                this.baselinesLoadError = 'Failed to load test project baselines';
                console.error(error);
            }

            return false;
        }
    }

    @action.bound
    async createBaselineForTopic(topicId: string, packageId: string, value: string, fuzzy?: number) {
        const project = this.editableBaselineProject;

        if (!project) {
            return false;
        }

        if (!project.topics.some(topic => topic.topicId === topicId)) {
            return false;
        }

        if (!project.packageIds.includes(packageId)) {
            return false;
        }

        this.isSubmittingBaseline = true;

        try {
            const result = await this.service.createTestProjectBaseline(
                this.projectId,
                this.testProjectId,
                packageId,
                topicId,
                value,
                fuzzy
            );

            if (result.isErr()) {
                console.error('Failed to create baseline', result.error);
                return false;
            }

            this.testProjectBaselines = [...this.testProjectBaselines, result.value];
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            this.isSubmittingBaseline = false;
        }
    }

    @action.bound
    async updateBaselineById(baselineId: string, value: string, fuzzy?: number) {
        const project = this.editableBaselineProject;

        if (!project) {
            return false;
        }

        const baseline = this.testProjectBaselines.find(item => item.id === baselineId);

        if (!baseline || baseline.testProjectId !== this.testProjectId) {
            return false;
        }

        if (!project.topics.some(topic => topic.topicId === baseline.topicId)) {
            return false;
        }

        if (!project.packageIds.includes(baseline.packageId)) {
            return false;
        }

        this.isSubmittingBaseline = true;

        try {
            const result = await this.service.updateTestProjectBaseline(
                this.projectId,
                this.testProjectId,
                baselineId,
                value,
                fuzzy
            );

            if (result.isErr()) {
                console.error('Failed to update baseline', result.error);
                return false;
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            this.isSubmittingBaseline = false;
        }
    }

    @action.bound
    async exportTestProjectBaselines() {
        if (this.canUseProject) {
            await this.service.exportTestProjectBaselines(this.projectId, this.testProjectId);
        }
    }

    @action.bound
    async deletePackageFromTestProject(packageId: string, packageName: string) {
        const project = this.testProject;

        if (!this.canUseProject || !project || !project.packageIds.includes(packageId)) {
            return;
        }

        this.deletingPackageId = packageId;

        try {
            const packageIds = project.packageIds.filter(id => id !== packageId);
            const result = await this.service.updateTestProjectPackages(this.projectId, this.testProjectId, packageIds);

            if (result.isOk()) {
                message.success(`Document "${packageName}" successfully removed from the test project`);

                const currentProject = this.testProject;

                if (currentProject) {
                    this.testProject = { ...currentProject, packageIds };
                }
            } else {
                message.error(`Failed to remove document "${packageName}" from the test project`);
            }
        } finally {
            this.deletingPackageId = null;
        }
    }

    @action.bound
    handleTestProjectChanges(change: TestProjectChange) {
        const project = this.testProject;

        if (
            change.projectId !== this.projectId ||
            change.id !== this.testProjectId ||
            !project ||
            (project.isAborting && change.isProgressUpdate)
        ) {
            return;
        }

        this.testProject = {
            ...project,
            isRunning: change.isRunning,
            processedPackageCount: change.processedPackageCount,
            lastRunTime: change.lastRunTime,
            avgTopicProcessingTime: change.avgTopicProcessingTime,
            isAborting: change.isAborting,
            newPackageIds: change.isRunning === false ? (change.newPackageIds ?? []) : project.newPackageIds
        };
    }

    @action.bound
    markRunStarted() {
        if (this.testProject) {
            this.testProject = { ...this.testProject, isRunning: true };
        }
    }

    @action.bound
    markRunAborting() {
        if (this.testProject) {
            this.testProject = { ...this.testProject, isAborting: true };
        }
    }

    @action.bound
    removePackageFromNewPackages(packageId: string) {
        if (this.testProject?.newPackageIds) {
            this.testProject.newPackageIds = this.testProject.newPackageIds.filter(id => id !== packageId);
        }
    }

    @action.bound
    async handleInputsChanged(change: TestProjectInputsChanged) {
        if (change.projectId !== this.projectId || change.testProjectId !== this.testProjectId) {
            return;
        }

        this.inputsChange = change;
        this.inputRefreshStatus = 'idle';
        await this.retryInputRefresh();
    }

    @action.bound
    async retryInputRefresh() {
        const change = this.inputsChange;

        if (!change || this.inputRefreshStatus === 'loading') {
            return;
        }

        // Keep Dashboard reads lazy, but refresh any baseline list already requested, including failed reads.
        const baselinesRequested = this.baselinesStatus !== 'idle';
        this.inputRefreshStatus = 'loading';
        const results = await Promise.all([
            this.loadTestProject(),
            baselinesRequested ? this.loadTestProjectBaselines() : Promise.resolve(true)
        ]);

        if (change === this.inputsChange) {
            this.inputRefreshStatus = results.every(Boolean) ? 'ready' : 'error';
        }
    }

    private get editableBaselineProject() {
        return this.canEditBaselines ? this.testProject : undefined;
    }
}
